// Pure helpers for the Tech Support composer (a .ts sibling so the .tsx only
// exports the component — react-refresh/only-export-components).

import { normalizeHref, splitLinkSegments } from '../../utils/linkify';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Plain text → simple HTML paragraphs (blank line = new paragraph). */
export function textToEditorHtml(text: string): string {
  if (!text.trim()) return '';
  return text
    .split(/\n{2,}/)
    .map((para) => `<p>${escapeHtml(para).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** One link found in the draft body — shown in the composer's test strip. */
export interface DraftLink {
  /** Normalized, open-able URL (deduplication key). */
  href: string;
  /** The anchor's visible text, or the URL itself for bare/text URLs. */
  label: string;
}

/**
 * Every link currently in the draft the editor holds: real `<a>` anchors
 * first, then bare URLs sitting in plain text (agent drafts often carry
 * un-anchored URLs). Deduped by normalized href so the test strip stays
 * short even when a URL appears in several places.
 */
export function extractDraftLinks(html: string): DraftLink[] {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const links: DraftLink[] = [];
  const seen = new Set<string>();
  const push = (rawHref: string, label: string) => {
    const href = normalizeHref(rawHref);
    if (!href || seen.has(href)) return;
    seen.add(href);
    links.push({ href, label: label.trim() || href });
  };
  doc.body.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href') ?? '';
    if (!href || href.startsWith('#')) return;
    push(href, a.textContent ?? '');
  });
  // Strip the anchors we already collected, then scan the remaining plain
  // text for bare URLs — they are exactly the links John most wants to test.
  doc.body.querySelectorAll('a').forEach((a) => a.remove());
  // textContent glues adjacent blocks together ("…/x.php" + "It is…" →
  // "…/x.phpIt"), so mark block boundaries with newlines before scanning.
  doc.body
    .querySelectorAll('p, div, li, br, h1, h2, h3, h4, blockquote, td, tr')
    .forEach((el) => {
      if (el.tagName === 'BR') el.insertAdjacentText('afterend', '\n');
      else el.insertAdjacentText('beforeend', '\n');
    });
  for (const seg of splitLinkSegments(doc.body.textContent ?? '')) {
    if (seg.href) push(seg.href, seg.text);
  }
  return links;
}

/**
 * The href to open for a Ctrl/Cmd+Click inside the editor, or null when the
 * click should fall through to normal caret placement (no modifier, or the
 * click didn't land on a link). `target` is the raw DOM event target — the
 * click often lands on a nested element (e.g. <strong> inside the anchor).
 */
export function clickedLinkHref(
  target: EventTarget | null,
  withModifier: boolean,
): string | null {
  if (!withModifier || !(target instanceof Element)) return null;
  const href = target.closest('a[href]')?.getAttribute('href')?.trim();
  return href ? normalizeHref(href) : null;
}

/** "a@x.com, B <b@y.com>; c@z.com" → ["a@x.com", "b@y.com", "c@z.com"] */
export function parseAddressList(raw: string): string[] {
  return raw
    .split(/[,;]/)
    .map((part) => {
      const angled = part.match(/<([^>]+)>/);
      return (angled ? angled[1] : part).trim();
    })
    .filter((a) => a.includes('@'));
}

/**
 * Give a pasted clipboard image a distinct, sortable filename. Windows
 * snips arrive as a File literally named "image.png" (or unnamed), and the
 * draft's attachment keep-list matches by (filename, size) — so two pastes
 * with the same generic name could collide. A real filename (a file copied
 * in Explorer and pasted) is kept as-is.
 */
export function namePastedFile(file: File, seq: number, now: Date = new Date()): File {
  const generic = !file.name || /^image\.[a-z0-9]+$/i.test(file.name);
  if (!generic) return file;
  const ext = (file.type.split('/')[1] || 'png').toLowerCase().replace('jpeg', 'jpg');
  const stamp = now
    .toISOString()
    .slice(0, 23) // to the millisecond — rapid double-pastes stay distinct
    .replace(/[-:.]/g, '')
    .replace('T', '-');
  return new File([file], `pasted-image-${stamp}-${seq}.${ext}`, { type: file.type });
}

/**
 * Every file on a paste/drop DataTransfer — the Windows-snip case first.
 *
 * Reads `items` (kind === 'file' → getAsFile()) like the Merlin chat composer
 * does, then falls back to `files`: Chromium fills both for a snip, but some
 * clipboard sources populate only one, and reading just `files` (the old
 * composer path) is exactly the kind of gap that makes a paste look ignored.
 * Text-only clipboards yield [].
 */
export function pastedFiles(data: DataTransfer | null | undefined): File[] {
  if (!data) return [];
  const out: File[] = [];
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind !== 'file') continue;
    const f = item.getAsFile();
    if (f) out.push(f);
  }
  if (out.length === 0) out.push(...Array.from(data.files ?? []));
  return out;
}

// Local parts that mark an address nothing human reads. MIRRORS
// `_UNREPLYABLE_LOCAL_PREFIXES` in services/email/reply_recipient.py — matched as
// a prefix there and here, so `noreply_invoices@` and `no-reply+tag@` both land.
export const UNREPLYABLE_LOCAL_PREFIXES = [
  'noreply',
  'no-reply',
  'no_reply',
  'no.reply',
  'donotreply',
  'do-not-reply',
  'do_not_reply',
  'mailer-daemon',
  'mailerdaemon',
  'postmaster',
  'bounce',
];

/**
 * True when a reply sent to `address` cannot reach a human — a no-reply-style
 * address, or the bound mailbox itself (replying to it drops the message back
 * into the same inbox).
 *
 * This is a MIRROR of `is_unreplyable_address` in
 * services/email/reply_recipient.py. The BACKEND is authoritative: it refuses
 * the send with a 422 whatever this returns. This exists only so John sees the
 * problem before he writes a whole reply and clicks Send, instead of after.
 * The shared cases are pinned in composerUtils.test.ts — keep them in step.
 */
export function isUnreplyableAddress(
  address: string | null | undefined,
  accountEmail?: string | null,
): boolean {
  const addr = parseAddressList(address ?? '')[0];
  if (!addr) return false; // empty is "not yet filled in", not "unreplyable"
  if (accountEmail && addr.toLowerCase() === accountEmail.trim().toLowerCase()) {
    return true;
  }
  const local = addr.slice(0, addr.lastIndexOf('@')).toLowerCase();
  return UNREPLYABLE_LOCAL_PREFIXES.some((p) => local.startsWith(p));
}
